import { useId } from "react";

import type { ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { ErrorNotice, Skeleton } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { formatCount, plural } from "../../lib/format";
import type { ImportIssue, ValidationReport } from "../../types/api";
import { toleranceError } from "./wizardState";

interface ValidationStepProps {
  report: ValidationReport | undefined;
  loading: boolean;
  error: ApiError | undefined;
  settings: { money: string; weight: string };
  running: boolean;
  runError: ApiError | null;
  onSettings: (change: { money?: string; weight?: string }) => void;
  onRetry: () => void;
  onBack: () => void;
  onRun: () => void;
}

function IssueList({ issues }: { issues: ImportIssue[] }) {
  return (
    <ul className="issue-list">
      {issues.map((issue, index) => (
        <li key={`${issue.file}-${issue.row}-${issue.code}-${index}`}>
          <span className="issue-list__file">{issue.file === "shipment" ? "Shipment file" : "Invoice file"}</span>
          {issue.message}
        </li>
      ))}
    </ul>
  );
}

function ToleranceInput({ label, prefix, suffix, value, error, hint, onChange }: {
  label: string;
  prefix?: string;
  suffix?: string;
  value: string;
  error: string | null;
  hint: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="tolerance">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <div className={`affix-input${error ? " affix-input--error" : ""}`}>
        {prefix ? <span aria-hidden="true">{prefix}</span> : null}
        <input
          id={id}
          inputMode="decimal"
          value={value}
          aria-invalid={Boolean(error)}
          aria-describedby={`${id}-hint`}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix ? <span aria-hidden="true">{suffix}</span> : null}
      </div>
      <p id={`${id}-hint`} className={error ? "field-error" : "field-hint"}>
        {error ?? hint}
      </p>
    </div>
  );
}

export function ValidationStep(props: ValidationStepProps) {
  const { report, loading, error, settings, running, runError, onSettings, onRetry, onBack, onRun } = props;
  const moneyError = toleranceError(settings.money, "money");
  const weightError = toleranceError(settings.weight, "weight");
  const errors = report?.issues.filter((issue) => issue.severity === "error") ?? [];
  const warnings = report?.issues.filter((issue) => issue.severity === "warning") ?? [];
  const canRun = Boolean(report?.ready) && !loading && !moneyError && !weightError;

  return (
    <section aria-labelledby="validation-heading">
      <div className="step-heading">
        <h1 id="validation-heading">Check before you audit</h1>
        <p className="muted">A last look at what FreightAudit will compare, so garbage never reaches the results.</p>
      </div>

      {error ? <ErrorNotice error={error} onRetry={onRetry} /> : null}
      {loading && !report ? <Skeleton lines={6} label="Checking your files" /> : null}

      {report ? (
        <div className="validation-grid" aria-busy={loading}>
          <div className="panel readiness">
            <p className={`readiness__title${report.ready ? " readiness__title--ok" : " readiness__title--blocked"}`}>
              <Icon name={report.ready ? "check" : "alert"} size={20} />
              {report.ready ? "Ready to audit" : `${plural(report.error_count, "problem")} to fix first`}
            </p>
            <dl className="stat-list">
              <div>
                <dt>Shipment records</dt>
                <dd>{formatCount(report.shipments.records)}</dd>
              </div>
              <div>
                <dt>Invoice rows</dt>
                <dd>{formatCount(report.invoices.audited)}</dd>
              </div>
              <div>
                <dt>Recognized PROs</dt>
                <dd>{formatCount(report.invoices.recognized)}</dd>
              </div>
              <div>
                <dt>PROs with no shipment</dt>
                <dd>{formatCount(report.invoices.unmatched)}</dd>
              </div>
              <div>
                <dt>Blank PROs</dt>
                <dd>{formatCount(report.invoices.blank_pro)}</dd>
              </div>
              <div>
                <dt>Rows with duplicate PROs</dt>
                <dd>{formatCount(report.invoices.duplicate_pro_rows)}</dd>
              </div>
            </dl>
            {errors.length ? (
              <div className="issues issues--errors">
                <p className="issues__title">
                  Fix these in your spreadsheet, then upload it again
                  {report.error_count > errors.length ? ` (showing ${errors.length} of ${report.error_count})` : ""}:
                </p>
                <IssueList issues={errors} />
              </div>
            ) : null}
            {warnings.length ? (
              <details className="issues issues--warnings">
                <summary>
                  {plural(report.warning_count, "note")} that won&apos;t block the audit
                </summary>
                <IssueList issues={warnings} />
              </details>
            ) : null}
          </div>

          <div className="panel settings-panel">
            <h2>Audit settings</h2>
            <ToleranceInput
              label="Money tolerance"
              prefix="$"
              value={settings.money}
              error={moneyError}
              hint="Billed totals within this amount of the quote pass."
              onChange={(money) => onSettings({ money })}
            />
            <ToleranceInput
              label="Weight tolerance"
              suffix="lb"
              value={settings.weight}
              error={weightError}
              hint="Billed weights within this many pounds pass."
              onChange={(weight) => onSettings({ weight })}
            />
            <p className="field-hint">Saved with the audit, so every flag stays explainable later.</p>
          </div>
        </div>
      ) : null}

      {runError ? <ErrorNotice error={runError} /> : null}

      <div className="step-actions">
        <Button variant="ghost" icon="arrow-left" onClick={onBack} disabled={running}>
          Back
        </Button>
        <Button variant="primary" onClick={onRun} disabled={!canRun} busy={running}>
          {running ? `Auditing ${report ? plural(report.invoices.audited, "invoice") : "invoices"}…` : "Run audit"}
        </Button>
      </div>
    </section>
  );
}
